import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ConsentDocStatus } from '../lib/api';
import { isAcceptEnabled, isGrandfatherPrompt } from '../lib/consentGateState';
import signInStyles from './SignIn.module.css';
import styles from './ConsentGate.module.css';

interface ConsentGateProps {
  statuses: ConsentDocStatus[];
  phase: 'idle' | 'accepting' | 'error' | 'done';
  error: string | null;
  onAccept: () => void;
  /** "decline = signed out politely with their data intact" (the mission's
   *  own explicit anti-dark-pattern requirement) — wired to the SAME
   *  useAuth().signOut every other sign-out affordance in this app already
   *  calls (POST /auth/logout; nothing about the user's account or data is
   *  touched by declining, only the session ends). */
  onDecline: () => void;
}

/**
 * The single warm consent screen (ENROLL lane — "a user whose consents are
 * missing/stale hits a single warm consent screen"). Rendered by App.tsx
 * whenever useConsentGate reports needsAction, for BOTH a brand-new signup
 * (state 'missing' on every doc — the ordinary first-run enrollment path)
 * and an existing, grandfathered user hitting a version bump on next sign-in
 * (state 'stale' on at least one doc, via='grandfather_prompt' — see
 * isGrandfatherPrompt/consentGateState.ts). Same screen, same component: the
 * only thing that differs is the opening line's framing (a returning user
 * reads "the terms have changed" rather than "before we get started").
 *
 * ONE combined checkbox, not two independent ones: both docs (EULA +
 * Privacy Policy) are ALWAYS presented and accepted together in this flow —
 * there is no product path where a user could sensibly use the app having
 * accepted only one of the two, so splitting the checkbox would only add
 * friction without adding a real choice. useConsentGate.acceptAll POSTs both
 * docs that are actually pending (never one already 'current' — a doc a
 * user already accepted this exact version of is left untouched).
 *
 * Shares SignIn.module.css's page/card/halo/avatarLarge/title shell — same
 * visual family as the front door and Goodbye, one source of truth, zero
 * style drift (mirrors Goodbye.tsx's own precedent for reusing that file).
 */
export function ConsentGate({ statuses, phase, error, onAccept, onDecline }: ConsentGateProps) {
  const [checked, setChecked] = useState(false);
  const grandfather = isGrandfatherPrompt(statuses);
  const acceptEnabled = isAcceptEnabled(checked, phase);

  return (
    <div className={signInStyles.page}>
      <div className={signInStyles.card}>
        <div className={signInStyles.halo}>
          <div className={signInStyles.avatarLarge}>M</div>
        </div>
        <div className={signInStyles.title}>
          {grandfather ? "I've updated a couple of things." : 'One thing before we start.'}
        </div>
        <div className={signInStyles.body}>
          {grandfather
            ? "My terms and privacy policy have changed since you last signed in. Take a look, and if you're still with me, just say so."
            : "I keep your career story, and I want you to know exactly what that means before you tell it to me. Here's my end user license agreement and my privacy policy — plain language, no fine-print tricks."}
        </div>

        <div className={styles.docsRow}>
          <Link to="/terms" target="_blank" rel="noreferrer" className={styles.docLink}>
            Read the End User License Agreement
          </Link>
          <Link to="/privacy" target="_blank" rel="noreferrer" className={styles.docLink}>
            Read the Privacy Policy
          </Link>
        </div>

        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            disabled={phase === 'accepting'}
          />
          <span className={styles.checkboxLabel}>
            I've read and accept the End User License Agreement and the Privacy Policy.
          </span>
        </label>

        <button
          type="button"
          className={styles.acceptButton}
          onClick={onAccept}
          disabled={!acceptEnabled}
        >
          {phase === 'accepting' ? (
            <>
              <span className={styles.spinner} aria-hidden="true" />
              Just a moment…
            </>
          ) : (
            'Accept and continue'
          )}
        </button>

        {phase === 'error' && error ? (
          <div className={styles.errorRow} role="alert">
            That didn't go through: {error}. Mind trying again?
          </div>
        ) : null}

        <button type="button" className={styles.declineButton} onClick={onDecline}>
          Not right now — sign me out
        </button>
      </div>
    </div>
  );
}
